export enum KnowledgeOwnerType {
  TEAM = 'TEAM',
  USER = 'USER',
}

// PBAC — visibility and participant types

export enum KnowledgeSpaceVisibility {
  TEAM = 'TEAM',
  RESTRICTED = 'RESTRICTED',
}

export enum KnowledgeSpaceRole {
  VIEWER = 'VIEWER',
  EDITOR = 'EDITOR',
  MANAGER = 'MANAGER',
}

export interface KnowledgeSpaceParticipant {
  userId: string;
  role: KnowledgeSpaceRole;
}

/** View participant — enriched with user profile fields for GraphQL responses */
export interface KnowledgeSpaceParticipantView {
  id: string; // userId
  name: string;
  email: string;
  title: string;
  role: KnowledgeSpaceRole;
}

export enum KnowledgeAuthorKind {
  USER = 'USER',
  AGENT = 'AGENT',
}

export type KnowledgeAuthorRefValue =
  | { kind: KnowledgeAuthorKind.USER; userId: string }
  | { kind: KnowledgeAuthorKind.AGENT; agentId: string; actingUserId: string };

export interface KnowledgeSpaceItem {
  ownerId: string; // PK — equals teamId or userId based on ownerType
  spaceId: string; // SK
  ownerType: KnowledgeOwnerType;
  teamId?: string; // present when ownerType=TEAM (denormalized = ownerId)
  userId?: string; // present when ownerType=USER (denormalized = ownerId)
  name: string;
  isSystem: boolean; // true for the 3 system spaces auto-created on team create
  pageCount: number;
  createdAt: number; // unix ms
  updatedAt: number;
  // PBAC fields (all optional for backward compat with pre-PBAC rows)
  createdBy?: string; // immutable audit — userId of creator
  ownerUserId?: string; // mutable current owner (TEAM spaces only)
  visibility?: KnowledgeSpaceVisibility; // defaults to TEAM when absent
  participants?: KnowledgeSpaceParticipant[];
  version?: number; // optimistic lock counter
  /** wrapped DEK for `name`; absent on pre-envelope rows */
  encryptedDataKey?: string;
  // Readable: structural flag the engine branches on (only the Brain projection writes pages).
  brainManaged?: boolean;
}

export interface KnowledgeFavoriteItem {
  userId: string; // PK
  pageId: string; // SK — stable across cross-space moves
  spaceId: string; // denormalized locator — required to BatchGet the page
  ownerType: KnowledgeOwnerType; // denormalized at favorite time
  favoritedAt: number; // unix ms
}

export enum KnowledgePageMoveOperationKind {
  MOVE_OUT = 'MOVE_OUT', // marks page item in source before Delete
  MOVE_IN = 'MOVE_IN', // marks page item in target in the Put
}

export interface KnowledgePageMoveOperation {
  kind: KnowledgePageMoveOperationKind;
  txId: string;
  sourceSpaceId?: string; // present when kind === MOVE_IN
  targetSpaceId?: string; // present when kind === MOVE_OUT
}

export interface KnowledgePageAttachment {
  id: string; // UUID7, generated at presigned URL step
  fileName: string; // original file name (read from S3 tag OriginalFileName)
  s3Key: string; // '{pageId}/{attachmentId}' — not exposed in GraphQL schema
  mimeType: string; // MIME type declared at presigned URL generation
  sizeBytes: number; // file size in bytes (read from S3 object metadata)
  uploadedBy: string; // userId
  uploadedAt: number; // unix ms
  url?: string; // presigned GET URL — generated in resolver, never persisted
}

export interface KnowledgePageImage {
  id: string; // UUID7
  s3Key: string; // '{pageId}/{imageId}' — not exposed in GraphQL schema
  mimeType: string; // image/png | image/jpeg | image/gif | image/webp
  fileName: string; // original file name (read from S3 tag OriginalFileName)
  uploadedBy: string; // userId
  uploadedAt: number; // unix ms
  url?: string; // presigned GET URL — generated in field resolver, never persisted
}

export interface KnowledgePageItem {
  spaceId: string; // PK
  pageId: string; // SK
  ownerId: string; // denormalized from Space
  ownerType: KnowledgeOwnerType;
  parentPageId?: string; // absent on root pages
  title: string;
  path: string; // materialized slug path; stable on rename
  depth: number; // 0..10
  currentVersion: number;
  currentS3Key: string;
  childCount: number;
  createdAt: number;
  createdBy: KnowledgeAuthorRefValue; // discriminated USER | AGENT
  updatedAt: number;
  attachments?: KnowledgePageAttachment[]; // absent = [] in UI
  images?: KnowledgePageImage[]; // inline images; absent = [] in UI
  isSystem?: boolean; // true for protected system pages (e.g. DoBox Rules); absent ⇒ false
  /** transient — only present during a move operation */
  _moveOperation?: KnowledgePageMoveOperation;
  /** wrapped DEK for `title` and the file names; absent on pre-envelope rows */
  encryptedDataKey?: string;
}

export interface KnowledgePageVersionItem {
  pageId: string; // PK
  version: number; // SK (numeric, sequential from 1)
  ownerId: string; // denormalized
  ownerType: KnowledgeOwnerType;
  spaceId: string; // denormalized at snapshot time
  teamId?: string; // denormalized when ownerType=TEAM
  s3Key: string;
  sizeBytes: number;
  authors: KnowledgeAuthorRefValue[]; // discriminated USER | AGENT; plural for forward-compat with Yjs CRDT
  timestamp: number;
  parentVersion?: number; // version - 1; absent on version 1
  summary: KnowledgeDiffSummary;
  changeMessage?: string; // free-text or "Restored from v<K>"
  /** wrapped DEK for `changeMessage`; absent when no changeMessage was set */
  encryptedDataKey?: string;
}

export interface KnowledgeDiffSummary {
  linesAdded: number;
  linesRemoved: number;
  charsChanged: number;
}

export interface KnowledgeUserRef {
  userId: string;
  email: string; // empty string for missing/deleted users
  displayName: string; // fullName, falling back to email then userId
  avatarUrl: string | null; // always null in v1 — see §1.9 open questions
}

/**
 * Agent identity descriptor returned alongside AGENT author refs.
 */
export interface KnowledgeAgentRef {
  agentId: string; // stable persona id, not per-run
  displayName: string;
  kind: string; // open enum — extensible per agent persona
}

/**
 * GraphQL view of an author identity. Resolved from
 * KnowledgeAuthorRefValue at the resolver boundary.
 * USER variant inflates userId → KnowledgeUserRef.
 * AGENT variant returns the agent's display info AND the inflated user
 * the agent acted on behalf of (actingUserId → actingUser).
 */
export type KnowledgeAuthorRef =
  | { kind: KnowledgeAuthorKind.USER; user: KnowledgeUserRef }
  | {
      kind: KnowledgeAuthorKind.AGENT;
      agent: KnowledgeAgentRef;
      actingUser: KnowledgeUserRef;
    };

export interface KnowledgeSpaceUserRef {
  id: string;
  name?: string;
  email?: string;
  title?: string;
}

export interface KnowledgeSpace {
  spaceId: string;
  ownerType: KnowledgeOwnerType;
  ownerId: string;
  name: string;
  isSystem: boolean;
  pageCount: number;
  createdAt: number;
  updatedAt: number;
  pages: KnowledgePageMeta[];
  // PBAC view fields — enriched at the resolver boundary
  visibility?: KnowledgeSpaceVisibility;
  createdBy?: KnowledgeSpaceUserRef;
  owner?: KnowledgeSpaceUserRef;
  participants?: KnowledgeSpaceParticipantView[];
  version?: number;
  effectiveRole?: KnowledgeSpaceRole | null;
  brainManaged?: boolean;
}

export interface KnowledgePageMeta {
  pageId: string;
  spaceId: string;
  parentPageId?: string;
  title: string;
  currentVersion: number;
  childCount: number;
  createdAt: number;
  updatedAt: number;
  isFavorited?: boolean;
  isSystem?: boolean; // true for protected system pages (e.g. DoBox Rules); absent ⇒ false
}

export interface KnowledgeFavoritePayload {
  pageId: string;
  spaceId: string;
  // Always set on INSERT/MODIFY — the receiver skips emitting when the page lookup misses.
  page?: KnowledgePageMeta;
}

export interface KnowledgeSpaceList {
  spaces: KnowledgeSpace[];
  exclusiveStartKey?: string;
}

export interface KnowledgePage {
  pageId: string;
  spaceId: string;
  ownerType: KnowledgeOwnerType;
  ownerId: string;
  parentPageId?: string;
  title: string;
  path: string;
  depth: number;
  currentVersion: number;
  childCount: number;
  createdAt: number;
  createdBy: KnowledgeAuthorRef; // USER or AGENT
  updatedAt: number;
  /** Raw (un-hydrated) attachments carried by buildKnowledgePage for the field resolver. */
  attachments?: KnowledgePageAttachment[];
  /** Raw (un-hydrated) images carried by buildKnowledgePage for the field resolver. */
  images?: KnowledgePageImage[];
  isFavorited?: boolean;
  isSystem?: boolean; // true for protected system pages (e.g. DoBox Rules); absent ⇒ false
  // Only set on REMOVE cross-space fan-out — signals relocation, not deletion.
  movedToSpaceId?: string;
}

export interface KnowledgePageContent {
  page: KnowledgePage; // ALWAYS the live page metadata (current title/path/childCount/etc.)
  content: string; // markdown body of the requested version (current if none specified)
  versionInfo: KnowledgeVersionInfo; // metadata of the version whose content was returned
}

export interface KnowledgeVersionInfo {
  version: number;
  isCurrentVersion: boolean; // true when caller didn't pass version
  authors: KnowledgeAuthorRef[]; // authors of THIS version (USER or AGENT, inflated from versionItem.authors)
  timestamp: number;
  sizeBytes: number;
  summary: KnowledgeDiffSummary; // { linesAdded, linesRemoved, charsChanged }
  parentVersion: number | null; // null on version 1
  changeMessage: string | null;
}

// ============================================================
// Mutation input shapes
// ============================================================

/** Input shape for a participant supplied at space-creation time. */
export interface KnowledgeSpaceParticipantInput {
  userId: string;
  role: KnowledgeSpaceRole;
}

export interface CreateKnowledgeSpaceInput {
  ownerType: KnowledgeOwnerType;
  /**
   * Required when ownerType is TEAM (the target team id).
   * MUST be omitted when ownerType is USER — derived from the authenticated
   * context. Supplying it for a USER space throws KnowledgeSpaceOwnerIdInputError.
   */
  ownerId?: string;
  name: string;
  /**
   * Required for TEAM spaces; ignored/omitted for USER spaces (enforced at runtime).
   * RESTRICTED with zero participants is valid — represents an owner-only private space.
   */
  visibility?: KnowledgeSpaceVisibility;
  /**
   * Optional initial participants. Allowed on TEAM spaces only — supplying
   * this on a USER space throws KnowledgeSpaceParticipantsNotAllowedOnUserSpaceError.
   */
  participants?: KnowledgeSpaceParticipantInput[];
}

export interface DeleteKnowledgeSpaceInput {
  spaceId: string;
}

export interface RenameKnowledgeSpaceInput {
  spaceId: string;
  name: string;
}

/** System-owned TEAM space (e.g. Company Brain areas); never reachable from a GraphQL input. */
export interface CreateKnowledgeSystemSpaceInput {
  teamId: string;
  name: string;
  ownerUserId: string;
  createdBy: string;
  brainManaged?: boolean;
}

/** System-owned page; `createKnowledgePage` cannot set isSystem, this is the only path that does. */
export interface CreateKnowledgeSystemPageInput {
  spaceId: string;
  title: string;
  ownerUserId: string;
  author?: KnowledgeAuthorRefValue;
  content?: string;
  /** Must itself be a system page of the same space. */
  parentPageId?: string;
  /** Fixed slug for a page found by path; defaults to the slugified title. */
  slug?: string;
}

export interface CreateKnowledgePageInput {
  spaceId: string;
  parentPageId?: string;
  title: string;
  content: string; // initial markdown
}

export interface EditKnowledgePageInput {
  pageId: string;
  spaceId: string;
  expectedVersion: number; // optimistic-lock token
  content: string; // full new markdown body
  changeMessage?: string;
}

export interface RestoreKnowledgePageVersionInput {
  pageId: string;
  spaceId: string;
  expectedVersion: number; // optimistic-lock token = current live version
  sourceVersion: number; // the historical version whose content is restored
}

export interface RenameKnowledgePageInput {
  pageId: string;
  spaceId: string;
  title: string;
}

export interface DeleteKnowledgePageInput {
  pageId: string;
  spaceId: string;
}

export interface MoveKnowledgePageInput {
  pageId: string;
  sourceSpaceId: string;
  targetSpaceId: string;
  targetParentPageId?: string; // null/omitted = root of target space
  expectedVersion: number; // optimistic lock on the root page
}

// ============================================================
// PBAC — Participant mutation input shapes
// ============================================================

export interface UpdateKnowledgeSpaceMembershipInput {
  spaceId: string;
  visibility: KnowledgeSpaceVisibility;
  participants: KnowledgeSpaceParticipantInput[];
  expectedVersion: number;
}

export interface TransferKnowledgeSpaceOwnershipInput {
  spaceId: string;
  newOwnerUserId: string;
}

export interface LeaveKnowledgeSpaceInput {
  spaceId: string;
}

// ============================================================
// Mutation result shapes
// ============================================================

export interface KnowledgeMutationResult {
  deletedId: string;
}

// ============================================================
// Knowledge — Diff types (knowledge-page-diff feature
// ============================================================

export enum KnowledgeDiffLineType {
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
  CONTEXT = 'CONTEXT',
}

export interface KnowledgeDiffLine {
  type: KnowledgeDiffLineType;
  content: string;
  baseLineNumber: number | null;
  headLineNumber: number | null;
}

export interface KnowledgeDiffHunk {
  baseStartLine: number;
  baseLineCount: number;
  headStartLine: number;
  headLineCount: number;
  lines: KnowledgeDiffLine[];
}

/**
 * GraphQL response shape for a between-version diff.
 * Returned by the knowledgePageDiff resolver..
 * baseAuthor / headAuthor are the inflated GraphQL view of the
 * persisted KnowledgeAuthorRefValue — same resolution path as
 * KnowledgeVersionInfo.author (USER → KnowledgeUserRef,
 * AGENT → KnowledgeAgentRef + actingUser).
 * summary reuses KnowledgeDiffSummary (linesAdded/linesRemoved/charsChanged).
 */
export interface KnowledgePageVersionDiff {
  baseVersion: number;
  headVersion: number;
  baseAuthors: KnowledgeAuthorRef[];
  headAuthors: KnowledgeAuthorRef[];
  baseTimestamp: number;
  headTimestamp: number;
  summary: KnowledgeDiffSummary;
  hunks: KnowledgeDiffHunk[];
}

// ============================================================
// Knowledge — Store-layer builder argument shapes
// ============================================================

export interface NewPageArgs {
  input: CreateKnowledgePageInput;
  space: { ownerId: string; ownerType: KnowledgeOwnerType; spaceId: string };
  pageId: string;
  slug: string;
  parentPage: KnowledgePageItem | null;
  now: number;
  author: KnowledgeAuthorRefValue;
  isSystem?: boolean; // true for protected system pages (e.g. DoBox Rules); absent ⇒ false
}

export interface NewVersionArgs {
  page: KnowledgePageItem;
  content: string;
  now: number;
  author: KnowledgeAuthorRefValue;
}

// ============================================================
// Knowledge — Store-layer raw diff shape (pre-author-inflation)
// ============================================================

/**
 * Store-layer diff shape: identical to KnowledgePageVersionDiff EXCEPT
 * baseAuthor / headAuthor are raw KnowledgeAuthorRefValue (USER | AGENT
 * discriminated). The resolver inflates these to KnowledgeAuthorRef
 * (USER → KnowledgeUserRef, AGENT → KnowledgeAgentRef + actingUser).
 *
 * The cast to KnowledgePageVersionDiff at the store boundary is intentional.
 */
export interface KnowledgePageVersionDiffRaw {
  baseVersion: number;
  headVersion: number;
  baseAuthors: KnowledgeAuthorRefValue[];
  headAuthors: KnowledgeAuthorRefValue[];
  baseTimestamp: number;
  headTimestamp: number;
  summary: KnowledgeDiffSummary;
  hunks: KnowledgeDiffHunk[];
}

export interface KnowledgePageVersionListRaw {
  versions: KnowledgePageVersionItem[];
  exclusiveStartKey?: string;
  currentVersion: number;
}

export interface KnowledgePageVersionList {
  versions: KnowledgeVersionInfo[];
  exclusiveStartKey?: string;
  currentVersion: number;
}
